import { useQueryClient } from "@tanstack/react-query";
import { useDemoSafeMutation } from "@/hooks/useDemoGuard";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { generateEntityInviteMessage, generateInviteLink } from "@/lib/whatsapp";
import { upsertInvitation } from "@/lib/invitations";
import { buildPhoneCandidates, normalizePhoneForDB } from "@/lib/phone";
import { assertPhoneAvailableAcrossCompanies } from "@/lib/crossCompanyCheck";
import { identityReserveUser, API_URL } from "@/services/api";
import { ownersRepository } from "@/repositories/owners";
import { tenantsRepository } from "@/repositories/tenants";
import { employeesRepository } from "@/repositories/employees";
import { serviceProvidersRepository } from "@/repositories/serviceProviders";
import type { AppRole } from "@/types/database";
import { CustomRole } from "@/lib/rbac";

export type UnifiedProfileType = "owner" | "tenant" | "employee" | "service_provider";

export interface UnifiedInviteParams {
  phone: string;
  fullName: string;
  profileType: UnifiedProfileType;
  /** Optional email — persisted to the entity row when provided (email-based invite flows). */
  email?: string;
  rolePermissionId?: string;
  customRoleName?: string;
  entityId?: string;
}

export interface UnifiedInviteResult {
  inviteLink: string;
  whatsappMessage: string;
  invitation: {
    id: string;
    token: string;
  };
}

const profileTypeToRole: Record<UnifiedProfileType, AppRole> = {
  owner: "owner",
  tenant: "tenant",
  employee: "employee",
  service_provider: "service_provider" as AppRole,
};

const entityToTableName = {
  owner: "owners",
  tenant: "tenants",
  employee: "employees",
  service_provider: "service_providers",
} as const;

export function useUnifiedInvite() {
  const { user, companyId } = useAuth();
  const { language } = useLanguage();
  const queryClient = useQueryClient();

  return useDemoSafeMutation({
    mutationFn: async (params: UnifiedInviteParams): Promise<UnifiedInviteResult> => {
      if (!companyId || !user?.id) {
        throw new Error("No company or user found");
      }

      if (!params.phone) {
        throw new Error("Phone number is required");
      }

      // Normalize to canonical (`966...` no `+`). Falls back to raw input
      // for non-Saudi numbers (UAE/KW/etc.) so multi-country invites still
      // function — those rows surface in `_phone_canon_anomalies`.
      const canonicalPhone = normalizePhoneForDB(params.phone) ?? params.phone;

      await assertPhoneAvailableAcrossCompanies(canonicalPhone, companyId, language as "ar" | "en");

      const role = profileTypeToRole[params.profileType];

      // Membership check (Phase 1 of orphan-prevention): user_roles is the
      // source of truth for "is this person already a member?". A user who
      // self-signed up via /owner-auth has user_roles but no invitations
      // row, so the upsertInvitation pending-dedupe alone misses them.
      // Block re-inviting an already-active member with a clear message.
      // Fail-CLOSED on RPC error: if we can't determine membership, we
      // refuse to invite rather than risk creating a stranded pending row.
      const { data: membership, error: membershipError } = await supabase.rpc(
        "check_user_membership_by_phone",
        { p_phone: canonicalPhone, p_company_id: companyId, p_role: role },
      );
      if (membershipError) {
        throw new Error(
          language === "ar"
            ? "تعذر التحقق من حالة عضوية المستخدم. حاول مرة أخرى."
            : "Could not verify user membership. Please try again.",
        );
      }
      const membershipFound =
        membership && typeof membership === "object" && !Array.isArray(membership)
          ? Boolean((membership as { found?: boolean }).found)
          : false;
      if (membershipFound) {
        throw new Error(
          language === "ar"
            ? `هذا المستخدم مسجل بالفعل بدور "${role}" في الشركة`
            : `This user is already a member of this company as ${role}`,
        );
      }

      // Per-profile default custom_role (matches the system role names seeded
      // into role_permissions and the validation trigger added in
      // 20260507121609_invitations_phone_primary_and_consistency_backfill).
      // Used as a fallback when the caller doesn't supply a customRoleName.
      const profileDefaultCustomRole: Record<UnifiedProfileType, string> = {
        owner: CustomRole.PropertyOwner,
        tenant: "tenant",
        employee: "property_manager",
        service_provider: "service_provider",
      };

      // When inviting without an existing directory row, create the row first
      // so the invitee shows up in the matching list (owners/tenants/employees/
      // providers) immediately as "Not Verified". Otherwise the invite is an
      // orphan: the user sees "invite created" but the corresponding tab stays
      // empty until the invitee accepts. On acceptance,
      // create_or_link_entity_for_invite links this row (related_user_id is
      // set) rather than inserting a duplicate.
      let resolvedEntityId = params.entityId ?? null;
      // Track a TENANT row CREATED by this call (not reused, not caller-supplied)
      // so we can roll it back if a later step throws — otherwise a failed invite
      // leaves an orphan that poisons the next attempt (the "data is used" trap).
      // Scoped to tenants: that's this ticket's flow, tenants soft-delete clears
      // the `deleted_at IS NULL` global-unique index (un-poisoning the retry),
      // and only `tenantsRepository` exposes a soft-delete. owners/employees keep
      // their pre-existing no-rollback behavior (no regression).
      let createdTenantId: string | null = null;
      if (!resolvedEntityId) {
        if (params.profileType === "owner") {
          const { data: newOwner, error: ownerErr } = await supabase
            .from("owners")
            .insert({
              company_id: companyId,
              full_name: params.fullName,
              phone: canonicalPhone,
              is_active: true,
              invitation_status: "invited",
            })
            .select("id")
            .single();
          if (ownerErr) throw ownerErr;
          resolvedEntityId = newOwner.id;

          // Phase 1: reserve a user identity (non-blocking)
          if (canonicalPhone && API_URL) {
            try {
              const { user_id } = await identityReserveUser({
                phone: `+${canonicalPhone}`,
                full_name: params.fullName,
                // Park-at-invite: grant the invited role + company up front so
                // the invitee can log in immediately (no /no-access).
                company_id: companyId,
                role,
                custom_role:
                  params.customRoleName ?? profileDefaultCustomRole[params.profileType],
              });
              await ownersRepository.update(newOwner.id, { user_id }, companyId);
            } catch {
              // Non-blocking — fallback at invite-accept still works.
            }
          }
        } else if (params.profileType === "tenant") {
          // Detect & reuse an existing same-company tenant first (e.g. an orphan
          // placeholder left by a failed prior invite) instead of inserting a
          // row that collides on the global-unique phone index. Cross-company
          // duplicates were already rejected by the assert above, so any match
          // here is same-company and safe to reuse.
          const reuseCandidates = buildPhoneCandidates(canonicalPhone);
          const existingTenant = reuseCandidates.length
            ? await tenantsRepository.findReusableByPhone(companyId, reuseCandidates)
            : null;

          if (existingTenant) {
            resolvedEntityId = existingTenant.id;
          } else {
            // tenant — split fullName into first/last on first whitespace;
            // single-word names get a single-space last_name so the NOT NULL
            // constraint is satisfied without rendering "undefined" anywhere.
            const trimmed = params.fullName.trim();
            const spaceIdx = trimmed.indexOf(" ");
            const firstName = spaceIdx === -1 ? trimmed : trimmed.slice(0, spaceIdx);
            const lastName = spaceIdx === -1 ? " " : trimmed.slice(spaceIdx + 1).trim() || " ";

            // tenants.id_number is NOT NULL with no default. Use an explicit
            // placeholder derived from the phone (which is already cross-company
            // unique-checked above) so the invited row never collides with a
            // real national ID. Replaced by the invitee when they accept.
            const placeholderIdNumber = `INVITED-${canonicalPhone}`;

            const { data: newTenant, error: tenantErr } = await supabase
              .from("tenants")
              .insert({
                company_id: companyId,
                tenant_type: "individual",
                first_name: firstName,
                last_name: lastName,
                phone: canonicalPhone,
                id_type: "national",
                id_number: placeholderIdNumber,
                notification_channel: "sms",
                is_active: true,
                invitation_status: "invited",
              })
              .select("id")
              .maybeSingle();
            if (tenantErr) throw tenantErr;
            if (!newTenant) throw new Error("Failed to create tenant record");
            resolvedEntityId = newTenant.id;
            createdTenantId = newTenant.id;
          }

          // Phase 1: reserve a user identity (non-blocking)
          if (!existingTenant && canonicalPhone && API_URL) {
            try {
              const { user_id } = await identityReserveUser({
                phone: `+${canonicalPhone}`,
                full_name: params.fullName,
                // Park-at-invite: grant the invited role + company up front so
                // the invitee can log in immediately (no /no-access).
                company_id: companyId,
                role,
                custom_role:
                  params.customRoleName ?? profileDefaultCustomRole[params.profileType],
              });
              await tenantsRepository.update(resolvedEntityId!, { user_id }, companyId);
            } catch {
              // Non-blocking — fallback at invite-accept still works.
            }
          }
        } else if (params.profileType === "employee") {
          // employees: only company_id + full_name are NOT NULL; user_id is
          // backfilled on acceptance. role_permission_id is wired below.
          // email is stored when the invite was initiated with an email address
          // (e.g. email-based invite flows); leave undefined for phone-only
          // invites so the column stays null (not overwritten to undefined).
          const { data: newEmployee, error: employeeErr } = await supabase
            .from("employees")
            .insert({
              company_id: companyId,
              full_name: params.fullName,
              phone: canonicalPhone,
              is_active: true,
              invitation_status: "invited",
              ...(params.email ? { email: params.email } : {}),
            })
            .select("id")
            .single();
          if (employeeErr) throw employeeErr;
          resolvedEntityId = newEmployee.id;

          // Phase 1: reserve a user identity (non-blocking)
          if (canonicalPhone && API_URL) {
            try {
              const { user_id } = await identityReserveUser({
                phone: `+${canonicalPhone}`,
                full_name: params.fullName,
                // Park-at-invite: grant the invited role + company up front so
                // the invitee can log in immediately (no /no-access).
                company_id: companyId,
                role,
                custom_role:
                  params.customRoleName ?? profileDefaultCustomRole[params.profileType],
              });
              await employeesRepository.update(newEmployee.id, companyId, { user_id: user_id });
            } catch {
              // Non-blocking — fallback at invite-accept still works.
            }
          }
        } else if (params.profileType === "service_provider") {
          // service_providers uses `name` (not full_name); user_id is nullable
          // and is backfilled here via the reserve-user park so verify-otp-d1
          // can resolve the auth.users row by phone at OTP verification time.
          const { data: newProvider, error: providerErr } = await supabase
            .from("service_providers")
            .insert({
              company_id: companyId,
              name: params.fullName,
              phone: canonicalPhone,
              is_active: true,
              invitation_status: "invited",
            })
            .select("id")
            .single();
          if (providerErr) throw providerErr;
          resolvedEntityId = newProvider.id;

          // Phase 1: reserve a user identity (non-blocking)
          if (canonicalPhone && API_URL) {
            try {
              const { user_id } = await identityReserveUser({
                phone: `+${canonicalPhone}`,
                full_name: params.fullName,
                // Park-at-invite: grant the invited role + company up front so
                // the invitee can log in immediately (no /no-access).
                company_id: companyId,
                role,
                custom_role:
                  params.customRoleName ?? profileDefaultCustomRole[params.profileType],
              });
              await serviceProvidersRepository.update(newProvider.id, companyId, { user_id });
            } catch {
              // Non-blocking — fallback at invite-accept still works.
            }
          }
        }
      }

      // From here on we may throw (e.g. upsertInvitation duplicate-detection). If
      // THIS call created the entity row above, roll it back on failure so a
      // failed invite doesn't strand an orphan that blocks the next attempt.
      try {
        // Upsert invitation via shared lib. Phone is the required identifier;
        // email is optional and is left unset here (no synthetic <phone>@phone.local).
        const fields: Record<string, unknown> = {
          full_name: params.fullName,
          phone: canonicalPhone,
          role,
          custom_role: params.customRoleName ?? profileDefaultCustomRole[params.profileType],
          invited_by: user.id,
          related_user_type: params.profileType,
          related_user_id: resolvedEntityId,
        };

        // Resolve the role: caller-supplied wins; otherwise fall back to the
        // company-scoped default for the invitee's profile type. The RPC
        // covers all four profile types (employee → property_manager,
        // owner → property_owner, tenant → tenant, service_provider →
        // service_provider) so the invite-accept flow always inserts a
        // matching user_role_assignments row regardless of which portal
        // the new user lands in.
        let resolvedRoleId = params.rolePermissionId;
        if (!resolvedRoleId) {
          const { data: defaultId } = await supabase.rpc(
            "get_default_role_for_profile_type",
            { p_profile_type: params.profileType, p_company_id: companyId },
          );
          if (defaultId) resolvedRoleId = defaultId as string;
        }

        if (resolvedRoleId) {
          fields.role_permission_id = resolvedRoleId;
        }

        const invitation = await upsertInvitation(supabase, companyId, fields as never);

        // Update entity's invitation_status if entityId provided
        if (params.entityId) {
          const tableName = entityToTableName[params.profileType];
          const { error: updateError } = await supabase
            .from(tableName)
            .update({ invitation_status: "invited" })
            .eq("id", params.entityId)
            .eq("company_id", companyId);

          if (updateError) {
            console.error(`Failed to update ${tableName} invitation_status:`, updateError.message ?? String(updateError));
          }
        }

        // Generate invite link and WhatsApp message
        const baseUrl = window.location.origin;
        const inviteLink = generateInviteLink(baseUrl, invitation.token);
        const whatsappMessage = generateEntityInviteMessage({
          name: params.fullName,
          entityType: params.profileType,
          inviteLink,
        }, language as "ar" | "en");

        return {
          inviteLink,
          whatsappMessage,
          invitation: {
            id: invitation.id,
            token: invitation.token,
          },
        };
      } catch (err) {
        // Roll back a tenant row we CREATED in this call so a failed invite
        // leaves no orphan to poison the next attempt. Soft-delete (sets
        // `deleted_at`) both respects the tenants soft-delete convention and
        // removes the row from the `deleted_at IS NULL` global-unique index, so
        // the retry succeeds. Best-effort. A reused / caller-supplied row is
        // never tracked here, so it is never deleted.
        if (createdTenantId) {
          await tenantsRepository.softDelete(createdTenantId, companyId).catch(() => {/* swallow rollback errors */});
        }
        throw err;
      }
    },
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({ queryKey: ["invitations"] });
      if (params.entityId) {
        queryClient.invalidateQueries({ queryKey: [entityToTableName[params.profileType]] });
      }

      if (params.profileType === "owner") {
        // useOwners() queries ["owners-with-stats", companyId] — the legacy
        // ["owners"] key isn't read anywhere, so invalidating it alone left
        // the list stale and newly-invited owners didn't appear.
        queryClient.invalidateQueries({ queryKey: ["owners-with-stats"] });
        queryClient.invalidateQueries({ queryKey: ["owners"] });
      } else if (params.profileType === "tenant") {
        queryClient.invalidateQueries({ queryKey: ["tenants"] });
        queryClient.invalidateQueries({ queryKey: ["tenants-stats"] });
        queryClient.invalidateQueries({ queryKey: ["tenants-stats-enhanced"] });
      } else if (params.profileType === "employee") {
        queryClient.invalidateQueries({ queryKey: ["employees"] });
      } else if (params.profileType === "service_provider") {
        queryClient.invalidateQueries({ queryKey: ["service-providers"] });
      }
    },
  });
}
